import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { ComingSoonPage } from '@components/ui/pages';
import { useBillingEntities } from '@features/billing-entities';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { SentryErrorBoundary } from '@services/SentryService';
import { FeatureGates, useFeatureGate } from '@services/statsig';
import {
  type EligibilityCheckResponse,
  enhancedApi as eligibilityApi,
} from '@store/generated/eligibility';
import { useAppDispatch } from '@store/hooks/core';

import { useBatchEligibilityPolling } from '../hooks/useBatchEligibilityPolling';
import type { BatchCheckResponse } from '../store/api';
import {
  dedupeByPatient,
  useGetUpcomingAppointmentsQuery,
  useRunBatchEligibilityMutation,
} from '../store/api';

import { BatchEligibilityTable } from './BatchEligibilityTable';

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function futureISO(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * Batch eligibility checks page.
 * Gated behind the ELIGIBILITY_CHECKS feature flag — shows "Coming Soon" when off.
 */
export const EligibilityChecksPage: React.FC = () => {
  const isEnabled = useFeatureGate(FeatureGates.ELIGIBILITY_CHECKS);

  if (!isEnabled) {
    return (
      <ComingSoonPage
        sx={{ justifyContent: 'center' }}
        title="Eligibility Checks"
        showHeader={false}
        fullHeight
        heroTitle="Eligibility Checks"
        description={
          <Typography component="p">
            Eligibility checks are coming soon. Check back later.
          </Typography>
        }
      />
    );
  }

  return (
    <SentryErrorBoundary
      fallback={(props) => (
        <Box sx={{ maxWidth: 1200, mx: 'auto', py: 3, px: 2 }}>
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => props.resetError()}>
                Retry
              </Button>
            }
          >
            Something went wrong loading eligibility checks.
          </Alert>
        </Box>
      )}
    >
      <BatchEligibilityContent />
    </SentryErrorBoundary>
  );
};

// ── Inner content (only rendered when feature is enabled) ────────────

const BatchEligibilityContent: React.FC = () => {
  const dispatch = useAppDispatch();

  // ── Billing entity check ───────────────────────────────────────────
  const { entities: billingEntities } = useBillingEntities();
  const hasDefaultBillingNpi = billingEntities.some(
    (e) => e.is_default && !!e.billing_npi,
  );

  // ── Date range: today → 14 days out ──────────────────────────────
  const [startDate] = useState(() => todayISO());
  const [endDate] = useState(() => futureISO(14));

  // ── Appointments query ───────────────────────────────────────────
  const { data: appointmentsData, isLoading: appointmentsLoading } =
    useGetUpcomingAppointmentsQuery({ startDate, endDate });

  const dedupedAppointments = useMemo(
    () => dedupeByPatient(appointmentsData?.items ?? []),
    [appointmentsData],
  );

  // ── Per-patient eligibility data ─────────────────────────────────
  const [eligibilityByPatient, setEligibilityByPatient] = useState<
    Record<string, EligibilityCheckResponse>
  >({});
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!dedupedAppointments.length) return;
    let aborted = false;

    const patientIds = dedupedAppointments
      .map((a) => a.patient?.patient_id)
      .filter(Boolean) as string[];

    setEligibilityByPatient({});

    // AppDispatch doesn't include RTK Query's enhanced return type, so we
    // cast to access .then() and .unsubscribe(). This is safe — initiate()
    // always returns a subscription promise when dispatched.
    type Subscription = Promise<{ data?: EligibilityCheckResponse }> & {
      unsubscribe: () => void;
    };

    const subscriptions = patientIds.map((patientId) => {
      const subscription = dispatch(
        eligibilityApi.endpoints.getV2ProviderPatientsByPatientIdEligibility.initiate(
          { patientId },
          { forceRefetch: true },
        ),
      ) as unknown as Subscription;

      subscription.then((result) => {
        if (!aborted && result.data) {
          const data = result.data;
          setEligibilityByPatient((prev) => ({ ...prev, [patientId]: data }));
        }
      });

      return subscription;
    });

    return () => {
      aborted = true;
      subscriptions.forEach((s) => s.unsubscribe());
    };
  }, [dedupedAppointments, dispatch, refreshKey]);

  // ── Batch mutation ───────────────────────────────────────────────
  const [runBatch, { isLoading: batchLoading }] = useRunBatchEligibilityMutation();
  const [batchResult, setBatchResult] = useState<BatchCheckResponse | null>(null);
  const [batchError, setBatchError] = useState<string | null>(null);
  const [activeBatchId, setActiveBatchId] = useState<string | null>(null);

  // ── Batch progress polling ───────────────────────────────────────
  const { progress, isPolling, isDone, isTimedOut } =
    useBatchEligibilityPolling(activeBatchId);

  // Refresh eligibility data when batch completes
  useEffect(() => {
    if (isDone && activeBatchId) {
      setRefreshKey((k) => k + 1);
    }
  }, [isDone, activeBatchId]);

  const handleRunBatch = useCallback(async () => {
    setBatchResult(null);
    setBatchError(null);
    setActiveBatchId(null);
    try {
      const result = await runBatch({ batchCheckRequest: {} }).unwrap();
      setBatchResult(result);
      // Only poll if there are actual checks to process
      if (result.actions_created > 0) {
        setActiveBatchId(result.batch_id);
      }
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'data' in err
          ? String((err as { data: { detail?: string } }).data?.detail ?? 'Unknown error')
          : 'Failed to run eligibility checks.';
      setBatchError(message);
    }
  }, [runBatch]);

  // ── Render ───────────────────────────────────────────────────────
  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', py: 3, px: 2 }}>
      <Stack spacing={3}>
        {/* Header */}
        <Box>
          <Typography variant="h5" gutterBottom>
            Eligibility Checks
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Check insurance eligibility for all patients with appointments in the next 2 weeks.
          </Typography>
        </Box>

        {/* Billing setup warning */}
        {!hasDefaultBillingNpi && (
          <Alert severity="warning">
            A default billing identity with an NPI is required to run eligibility checks.{' '}
            <Link component={RouterLink} to="/practice/billing-settings" color="inherit" fontWeight={600}>
              Configure one in Claim Submission Setup
            </Link>
          </Alert>
        )}

        {/* Action bar */}
        <Box>
          <Button
            variant="contained"
            color="primary"
            disabled={batchLoading || isPolling || !hasDefaultBillingNpi}
            onClick={handleRunBatch}
            startIcon={
              batchLoading ? <CircularProgress size={18} color="inherit" /> : undefined
            }
          >
            {batchLoading ? 'Starting…' : isPolling ? 'Checking…' : 'Run Eligibility Checks'}
          </Button>
        </Box>

        {/* Progress bar */}
        {isPolling && progress && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                Checking {progress.total} patients… {progress.completed}/{progress.total} complete
                {progress.failed > 0 && ` (${progress.failed} failed)`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {Math.round(progress.progress_pct)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress.progress_pct}
              aria-label="Eligibility check progress"
              sx={{ height: 8, borderRadius: 1 }}
            />
          </Box>
        )}

        {/* Alerts */}
        {batchResult && !isPolling && (
          <Alert
            severity={batchResult.actions_created === 0 ? 'info' : 'success'}
            onClose={() => setBatchResult(null)}
          >
            {batchResult.actions_created === 0
              ? batchResult.total_patients === 0
                ? 'No patients with upcoming appointments found.'
                : `All ${batchResult.total_patients} patients were recently checked — no new checks needed.`
              : isDone
                ? `Completed eligibility checks for ${progress?.completed ?? batchResult.actions_created} patients.${
                    batchResult.skipped_recently_checked > 0
                      ? ` ${batchResult.skipped_recently_checked} already up to date.`
                      : ''
                  }`
                : `Queued ${batchResult.actions_created} eligibility checks (${batchResult.skipped_recently_checked} already up to date).`}
            {progress && progress.failed > 0 && ` ${progress.failed} checks failed.`}
          </Alert>
        )}
        {batchError && (
          <Alert severity="error" onClose={() => setBatchError(null)}>
            {batchError}
          </Alert>
        )}
        {isTimedOut && (
          <Alert severity="warning">
            Polling timed out. Some checks may still be running in the background. Refresh the page
            to see updated results.
          </Alert>
        )}

        {/* Table */}
        <BatchEligibilityTable
          appointments={dedupedAppointments}
          eligibilityByPatient={eligibilityByPatient}
          loading={appointmentsLoading}
        />
      </Stack>
    </Box>
  );
};
